/**
 * One-class breakdowns over the summary file: by college, by conference,
 * and the players from one college in that class.
 */

import { CURRENT_YEAR, FIRST_YEAR } from '../constants/app';
import type { ClassSummaryFile, ClassSummaryRow } from '../types/classSummary';
import { PLAYER, ROW } from '../types/classSummary';
import {
  ClassAlignMode,
  ClassCompareFilters,
  classRowStatPoints,
  nflYearFor,
  rowMatchesClassFilters,
} from './classCompare';
import {
  CollegeMeta,
  ConferenceMeta,
  OTHER_CONFERENCE_ID,
  collegeIdForSlug,
  resolveCollegeMeta,
  resolveConferenceMeta,
} from './collegeMeta';
import { matchesFilters } from './filterUtils';

export const CLASS_CONFERENCE_SEGMENT = 'conference';

export type ClassDetailView = 'college' | 'conference' | 'players';

export interface ClassPathLocation {
  classYear: number;
  view: ClassDetailView;
  collegeSlug: string | null;
}

export interface ClassEntityRow {
  id: string;
  label: string;
  slug: string | null;
  logo: string | null;
  conferenceName: string | null;
  playerCount: number;
  values: number[];
  cumulative: number[];
  total: number;
}

export interface ClassEntityTable {
  classYear: number;
  group: 'college' | 'conference';
  align: ClassAlignMode;
  columns: number[];
  rows: ClassEntityRow[];
  ytdWeek: number;
  currentSeasonIncomplete: boolean;
  currentSeason: number;
  partial: boolean;
}

export interface ClassPlayerRow {
  playerIndex: number;
  name: string;
  pos: string | null;
  round: number | null;
  pick: number | null;
  values: number[];
  cumulative: number[];
  total: number;
}

export interface ClassPlayerTable {
  classYear: number;
  college: CollegeMeta;
  align: ClassAlignMode;
  columns: number[];
  rows: ClassPlayerRow[];
  ytdWeek: number;
  currentSeasonIncomplete: boolean;
  currentSeason: number;
  partial: boolean;
}

export const parseClassYearParam = (value: string | null | undefined): number | null => {
  if (!value) return null;
  const year = parseInt(value, 10);
  if (!Number.isInteger(year) || String(year) !== value.trim()) return null;
  if (year < 1970 || year > CURRENT_YEAR + 1) return null;
  return year;
};

export const parseClassPath = (pathname: string): ClassPathLocation | null => {
  const parts = pathname.split('/').filter(Boolean);
  if (parts[0] !== 'classes' || !parts[1]) return null;
  const classYear = parseClassYearParam(parts[1]);
  if (classYear == null) return null;
  if (!parts[2]) return { classYear, view: 'college', collegeSlug: null };
  if (parts[2] === CLASS_CONFERENCE_SEGMENT && !parts[3]) {
    return { classYear, view: 'conference', collegeSlug: null };
  }
  return { classYear, view: 'players', collegeSlug: parts[2] };
};

export const getClassDetailPath = (
  classYear: number,
  view: ClassDetailView = 'college',
  collegeSlug?: string | null
): string => {
  if (view === 'conference') return `/classes/${classYear}/${CLASS_CONFERENCE_SEGMENT}`;
  if (view === 'players' && collegeSlug) return `/classes/${classYear}/${collegeSlug}`;
  return `/classes/${classYear}`;
};

export const describeClassYear = (classYear: number): string => `Class of ${classYear}`;

const withCumulativeValues = (columns: number[], valuesByCol: Map<number, number>): {
  values: number[];
  cumulative: number[];
  total: number;
} => {
  const values = columns.map((col) => parseFloat((valuesByCol.get(col) || 0).toFixed(2)));
  const cumulative: number[] = [];
  values.reduce((sum, value) => {
    const next = parseFloat((sum + value).toFixed(2));
    cumulative.push(next);
    return next;
  }, 0);
  return {
    values,
    cumulative,
    total: cumulative.length ? cumulative[cumulative.length - 1] : 0,
  };
};

const walkClassRows = (
  summary: ClassSummaryFile,
  classYear: number,
  filters: ClassCompareFilters | null,
  visit: (row: ClassSummaryRow, points: number, nflYear: number, season: number) => void
): { maxNflYear: number; seasonKeys: Set<number> } => {
  let maxNflYear = 1;
  const seasonKeys = new Set<number>();

  for (const row of summary.rows) {
    const player = summary.players[row[ROW.playerIndex]];
    if (!player || player[PLAYER.classYear] !== classYear) continue;
    if (!rowMatchesClassFilters(row, player[PLAYER.round], filters)) continue;

    const season = row[ROW.season];
    const nflYear = nflYearFor(season, classYear);
    if (nflYear < 1) continue;
    if (nflYear > maxNflYear) maxNflYear = nflYear;
    seasonKeys.add(season);
    visit(row, classRowStatPoints(row, summary, filters?.stat || ''), nflYear, season);
  }

  return { maxNflYear, seasonKeys };
};

const columnsFor = (
  align: ClassAlignMode,
  maxNflYear: number,
  seasonKeys: Set<number>
): number[] =>
  align === 'season'
    ? Array.from(seasonKeys).sort((a, b) => a - b)
    : Array.from({ length: maxNflYear }, (_, i) => i + 1);

export const buildClassEntityTable = (
  summary: ClassSummaryFile,
  classYear: number,
  group: 'college' | 'conference',
  align: ClassAlignMode,
  filters: ClassCompareFilters | null = null,
  seasonColleges?: Record<string, { slug?: string; displayName?: string; logos?: Array<{ href?: string }> }> | null
): ClassEntityTable => {
  const totals = new Map<string, Map<number, number>>();
  const playersByEntity = new Map<string, Set<number>>();
  const metaByEntity = new Map<string, CollegeMeta | ConferenceMeta>();

  const { maxNflYear, seasonKeys } = walkClassRows(
    summary,
    classYear,
    filters,
    (row, points, nflYear, season) => {
      const collegeId = String(row[ROW.collegeId]);
      let entity: CollegeMeta | ConferenceMeta;
      if (group === 'college') {
        entity = resolveCollegeMeta(collegeId, seasonColleges);
      } else {
        entity = resolveConferenceMeta(resolveCollegeMeta(collegeId, seasonColleges).conferenceId);
      }
      if (!totals.has(entity.id)) {
        totals.set(entity.id, new Map());
        playersByEntity.set(entity.id, new Set());
        metaByEntity.set(entity.id, entity);
      }
      const key = align === 'season' ? season : nflYear;
      const bucket = totals.get(entity.id) as Map<number, number>;
      bucket.set(key, (bucket.get(key) || 0) + points);
      playersByEntity.get(entity.id)?.add(row[ROW.playerIndex]);
    }
  );

  const columns = columnsFor(align, maxNflYear, seasonKeys);
  const rows = Array.from(totals.entries())
    .map(([id, valuesByCol]) => {
      const meta = metaByEntity.get(id);
      const series = withCumulativeValues(columns, valuesByCol);
      return {
        id,
        label: meta?.name || id,
        slug: group === 'college' ? (meta as CollegeMeta).slug : null,
        logo: meta?.logo || null,
        conferenceName:
          group === 'college'
            ? resolveConferenceMeta((meta as CollegeMeta).conferenceId).name
            : meta?.name || null,
        playerCount: playersByEntity.get(id)?.size || 0,
        ...series,
      };
    })
    .filter((row) => row.total > 0 || row.playerCount > 0)
    .sort((a, b) => b.total - a.total || a.label.localeCompare(b.label));

  return {
    classYear,
    group,
    align,
    columns,
    rows,
    ytdWeek: summary.ytdWeek,
    currentSeasonIncomplete: summary.currentSeasonIncomplete,
    currentSeason: summary.toYear,
    partial: classYear < FIRST_YEAR,
  };
};

export const filterClassEntityTable = (
  table: ClassEntityTable,
  names: string[]
): ClassEntityTable => {
  if (!names.length) return table;
  const rows = table.rows.filter((row) =>
    matchesFilters(
      {
        college: { displayName: row.label },
        conference: row.conferenceName || undefined,
        name: row.slug || undefined,
      },
      names
    )
  );
  return { ...table, rows };
};

export const buildClassPlayerTable = (
  summary: ClassSummaryFile,
  classYear: number,
  collegeId: string,
  align: ClassAlignMode,
  filters: ClassCompareFilters | null = null,
  seasonColleges?: Record<string, { slug?: string; displayName?: string; logos?: Array<{ href?: string }> }> | null
): ClassPlayerTable => {
  const totals = new Map<number, Map<number, number>>();
  const posByPlayer = new Map<number, { pos: string | null; season: number }>();

  const { maxNflYear, seasonKeys } = walkClassRows(
    summary,
    classYear,
    filters,
    (row, points, nflYear, season) => {
      if (String(row[ROW.collegeId]) !== String(collegeId)) return;
      const playerIndex = row[ROW.playerIndex];
      if (!totals.has(playerIndex)) totals.set(playerIndex, new Map());
      const key = align === 'season' ? season : nflYear;
      const bucket = totals.get(playerIndex) as Map<number, number>;
      bucket.set(key, (bucket.get(key) || 0) + points);
      const prev = posByPlayer.get(playerIndex);
      if (!prev || season >= prev.season) {
        posByPlayer.set(playerIndex, { pos: row[ROW.pos], season });
      }
    }
  );

  const columns = columnsFor(align, maxNflYear, seasonKeys);
  const rows = Array.from(totals.entries())
    .map(([playerIndex, valuesByCol]) => {
      const player = summary.players[playerIndex];
      return {
        playerIndex,
        name: player?.[PLAYER.name] || `Player ${playerIndex}`,
        pos: posByPlayer.get(playerIndex)?.pos || null,
        round: player?.[PLAYER.round] ?? null,
        pick: player?.[PLAYER.pick] ?? null,
        ...withCumulativeValues(columns, valuesByCol),
      };
    })
    .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));

  return {
    classYear,
    college: resolveCollegeMeta(collegeId, seasonColleges),
    align,
    columns,
    rows,
    ytdWeek: summary.ytdWeek,
    currentSeasonIncomplete: summary.currentSeasonIncomplete,
    currentSeason: summary.toYear,
    partial: classYear < FIRST_YEAR,
  };
};

export const findCollegeIdForClass = (
  summary: ClassSummaryFile,
  classYear: number,
  slug: string,
  seasonColleges?: Record<string, { slug?: string; displayName?: string; logos?: Array<{ href?: string }> }> | null
): string | null => {
  const ids = new Set<string>();
  for (const row of summary.rows) {
    const player = summary.players[row[ROW.playerIndex]];
    if (player?.[PLAYER.classYear] === classYear) ids.add(String(row[ROW.collegeId]));
  }
  return collegeIdForSlug(slug, ids, seasonColleges);
};

export const formatDraftLabel = (round: number | null, pick: number | null): string => {
  if (round == null) return 'UDFA';
  return pick != null ? `R${round} #${pick}` : `R${round}`;
};

export const entityCellUsesYtd = (
  table: Pick<ClassEntityTable, 'align' | 'columns' | 'classYear' | 'currentSeason' | 'currentSeasonIncomplete'>,
  columnIndex: number
): boolean => {
  if (!table.currentSeasonIncomplete) return false;
  const column = table.columns[columnIndex];
  if (table.align === 'season') return column === table.currentSeason;
  return nflYearFor(table.currentSeason, table.classYear) === column;
};
